import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CanvasComponent } from '@/types';
import { useState } from 'react';

interface PropertiesPanelProps {
  component: CanvasComponent | null;
  onUpdate: (component: CanvasComponent) => void;
}

export default function PropertiesPanel({
  component,
  onUpdate,
}: PropertiesPanelProps) {
  const [selectedEvent, setSelectedEvent] = useState<string>('onClick');

  if (!component) {
    return (
      <Card className="w-64 p-4 m-4">
        <p className="text-muted-foreground text-center">
          Select a component to edit its properties
        </p>
      </Card>
    );
  }

  const handlePropertyChange = (
    property: string,
    value: string
  ) => {
    onUpdate({
      ...component,
      properties: {
        ...component.properties,
        [property]: value,
      },
    });
  };

  const handleEventChange = (
    eventType: string,
    code: string
  ) => {
    onUpdate({
      ...component,
      events: {
        ...component.events,
        [eventType]: code,
      },
    });
  };

  return (
    <Card className="w-64 p-4 m-4 space-y-4">
      <h2 className="font-semibold">Properties</h2>

      <div className="space-y-2">
        <Label>Name</Label>
        <Input
          value={component.properties.name}
          onChange={(e) => handlePropertyChange('name', e.target.value)}
        />
      </div>

      {component.type === 'image' ? (
        <div className="space-y-2">
          <Label>Source URL</Label>
          <Input
            value={component.properties.src}
            onChange={(e) => handlePropertyChange('src', e.target.value)}
          />
        </div>
      ) : (
        <div className="space-y-2">
          <Label>Text</Label>
          <Input
            value={component.properties.text}
            onChange={(e) => handlePropertyChange('text', e.target.value)}
          />
        </div>
      )}

      <div className="space-y-2">
        <Label>Event</Label>
        <Select
          value={selectedEvent}
          onValueChange={(value) => {
            setSelectedEvent(value);
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select event" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="onClick">onClick</SelectItem>
            <SelectItem value="onHover">onHover</SelectItem>
            <SelectItem value="onDrag">onDrag</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Event Code</Label>
        <Textarea
          value={component.events[selectedEvent] || ''}
          onChange={(e) => handleEventChange(selectedEvent, e.target.value)}
          placeholder="// Write your JavaScript code here"
          className="font-mono min-h-[150px] resize-y"
        />
      </div>

      <div className="space-y-2">
        <Label>Position</Label>
        <div className="grid grid-cols-2 gap-2">
          <Input
            type="number"
            value={component.position.x}
            onChange={(e) =>
              onUpdate({
                ...component,
                position: {
                  ...component.position,
                  x: parseInt(e.target.value) || 0,
                },
              })
            }
            placeholder="X"
          />
          <Input
            type="number"
            value={component.position.y}
            onChange={(e) =>
              onUpdate({
                ...component,
                position: {
                  ...component.position,
                  y: parseInt(e.target.value) || 0,
                },
              })
            }
            placeholder="Y"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Size</Label>
        <div className="grid grid-cols-2 gap-2">
          <Input
            type="number"
            value={component.size.width}
            onChange={(e) =>
              onUpdate({
                ...component,
                size: {
                  ...component.size,
                  width: parseInt(e.target.value) || 100,
                },
              })
            }
            placeholder="Width"
          />
          <Input
            type="number"
            value={component.size.height}
            onChange={(e) =>
              onUpdate({
                ...component,
                size: {
                  ...component.size,
                  height: parseInt(e.target.value) || 40,
                },
              })
            }
            placeholder="Height"
          />
        </div>
      </div>
    </Card>
  );
}